import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Plus, 
  RotateCcw, 
  Package, 
  AlertTriangle, 
  Clock,
  CheckCircle,
  User,
  Calendar,
  Pencil,
  FileText,
  Search,
  RefreshCw,
  Loader2,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: "active", label: "Active Rentals" },
  { value: "all", label: "All Rentals" },
  { value: "reserved", label: "Reserved" },
  { value: "approved", label: "Approved" },
  { value: "pending_delivery", label: "Pending Delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "return_scheduled", label: "Return Scheduled" },
  { value: "overdue", label: "Overdue" },
  { value: "returned", label: "Returned" },
  { value: "pending_approval", label: "Pending Approval" },
];

const STATUS_STYLES = {
  reserved: "bg-slate-100 text-slate-800",
  approved: "bg-green-100 text-green-800",
  pending_delivery: "bg-cyan-100 text-cyan-800",
  delivered: "bg-blue-100 text-blue-800",
  return_scheduled: "bg-purple-100 text-purple-800",
  returned: "bg-slate-200 text-slate-700",
  overdue: "bg-red-100 text-red-800",
  pending_approval: "bg-yellow-100 text-yellow-800",
  cancelled: "bg-red-50 text-red-600",
  declined: "bg-red-100 text-red-800",
  completed: "bg-emerald-100 text-emerald-800",
};

const RentalManagement = () => {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [rentalItems, setRentalItems] = useState([]);
  const [allRentalItems, setAllRentalItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewRentalOpen, setIsNewRentalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [returnRequests, setReturnRequests] = useState([]);
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null });
  const [approveData, setApproveData] = useState({
    collection_date: "",
    delivery_date: "",
    replacement_item_id: "",
    admin_notes: "",
  });
  
  const [newRentalData, setNewRentalData] = useState({
    item_id: "",
    customer_id: "",
    venue_id: "",
    quantity: 1,
    start_date: new Date().toISOString().split('T')[0],
    expected_return_date: "",
    condition_on_rental: "good",
    notes: "",
    assigned_technician_id: "",
  });
  
  const [editData, setEditData] = useState({
    customer_id: "",
    item_id: "",
    quantity: 1,
    start_date: "",
    expected_return_date: "",
    status: "",
    condition_on_rental: "",
    notes: "",
  });
  
  const [workers, setWorkers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [rentalVenues, setRentalVenues] = useState([]);
  const [replacementDialog, setReplacementDialog] = useState({ open: false, rental: null });
  const [replacementData, setReplacementData] = useState({
    item_id: "",
    quantity: 1,
    start_date: new Date().toISOString().split('T')[0],
    expected_return_date: "",
    assigned_technician_id: "",
    notes: "",
  });

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [customers]);

  const fetchData = useCallback(async () => {
    try {
      const [rentalsRes, itemsRes, allItemsRes, customersRes, requestsRes, workersRes, userRes] = await Promise.all([
        axios.get(`${API}/rentals`),
        axios.get(`${API}/rental-items/available`),
        axios.get(`${API}/rental-items`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/rental-return-requests/pending`),
        axios.get(`${API}/users/workers`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
      ]);
      setRentals(rentalsRes.data);
      setRentalItems(itemsRes.data);
      setAllRentalItems(allItemsRes.data);
      setCustomers(customersRes.data);
      setReturnRequests(requestsRes.data);
      setWorkers(workersRes.data || []);
      setCurrentUser(userRes.data);
    } catch (error) {
      toast.error("Failed to load rental data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch venues when customer changes in new rental form
  useEffect(() => {
    const fetchVenues = async () => {
      if (!newRentalData.customer_id) { setRentalVenues([]); return; }
      try {
        const res = await axios.get(`${API}/customers/${newRentalData.customer_id}/venues`);
        setRentalVenues(res.data);
      } catch (error) {
        console.error("Error fetching venues:", error);
        setRentalVenues([]);
      }
    };
    fetchVenues();
  }, [newRentalData.customer_id]);

  // Overdue auto-detection: check if expected_return_date has passed
  const isOverdue = useCallback((rental) => {
    if (rental.status === "returned" || rental.status === "completed") return false;
    if (!rental.expected_return_date) return false;
    const today = new Date().toISOString().split('T')[0];
    return rental.expected_return_date < today && rental.status !== "overdue";
  }, []);

  // Compute effective status (override to overdue if past due)
  const getEffectiveStatus = useCallback((rental) => {
    if (isOverdue(rental)) return "overdue";
    return rental.status;
  }, [isOverdue]);

  // Filtered rentals
  const filteredRentals = useMemo(() => {
    return rentals
      .filter((r) => {
        const effectiveStatus = getEffectiveStatus(r);
        // Status match:
        //   "all"     → every rental, including returned ones
        //   "active"  → every rental that hasn't been returned yet
        //   any other → exact status match
        let matchesStatus;
        if (statusFilter === "all") {
          matchesStatus = true;
        } else if (statusFilter === "active") {
          matchesStatus = effectiveStatus !== "returned";
        } else {
          matchesStatus = effectiveStatus === statusFilter;
        }
        const matchesSearch = !searchQuery ||
          r.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.rental_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.item_name?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [rentals, statusFilter, searchQuery, getEffectiveStatus]);

  // Stat counts with overdue auto-detection
  const statCounts = useMemo(() => {
    let active = 0, overdue = 0, pending = 0, delivered = 0;
    rentals.forEach((r) => {
      const eff = getEffectiveStatus(r);
      if (eff === "delivered") delivered++;
      if (eff === "overdue" || r.status === "overdue") overdue++;
      if (r.status === "pending_approval") pending++;
      if (eff !== "returned") active++;
    });
    return { active, overdue, pending, delivered, total: rentals.length };
  }, [rentals, getEffectiveStatus]);

  // Status counts for filter badges
  const statusCounts = useMemo(() => {
    const counts = {};
    let activeCount = 0;
    rentals.forEach((r) => {
      const eff = getEffectiveStatus(r);
      counts[eff] = (counts[eff] || 0) + 1;
      if (eff !== "returned") activeCount++;
    });
    counts.all = rentals.length;
    counts.active = activeCount;
    return counts;
  }, [rentals, getEffectiveStatus]);

  const handleCreateRental = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newRentalData };
      if (!payload.assigned_technician_id) delete payload.assigned_technician_id;
      await axios.post(`${API}/rentals`, payload);
      toast.success("Rental created successfully");
      setIsNewRentalOpen(false);
      resetNewRentalForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create rental");
    }
  };

  const handleAssignTechnician = async (rentalId, technicianId) => {
    try {
      await axios.post(`${API}/rentals/${rentalId}/assign-technician`, { technician_id: technicianId });
      toast.success("Technician assigned");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    }
  };

  const handleCreateReplacement = async () => {
    const rental = replacementDialog.rental;
    if (!rental) return;
    try {
      const payload = {
        item_id: replacementData.item_id || rental.item_id,
        customer_id: rental.customer_id,
        quantity: replacementData.quantity || rental.quantity,
        start_date: replacementData.start_date,
        expected_return_date: replacementData.expected_return_date || null,
        assigned_technician_id: replacementData.assigned_technician_id || null,
        notes: replacementData.notes || `Replacement for rental ${rental.rental_id}`,
        is_replacement: true,
        original_rental_id: rental.rental_id,
      };
      if (!payload.assigned_technician_id) delete payload.assigned_technician_id;
      if (!payload.expected_return_date) delete payload.expected_return_date;
      await axios.post(`${API}/rentals`, payload);
      toast.success("Replacement rental created");
      setReplacementDialog({ open: false, rental: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create replacement");
    }
  };

  const handleApproveReturnRequest = async () => {
    if (!approveDialog.request) return;
    try {
      await axios.post(`${API}/rental-return-requests/${approveDialog.request.request_id}/approve`, approveData);
      toast.success("Request approved");
      setApproveDialog({ open: false, request: null });
      setApproveData({ collection_date: "", delivery_date: "", replacement_item_id: "", admin_notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve request");
    }
  };

  const handleRecalculateStock = async () => {
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/rental-items/recalculate-inventory`);
      const data = res.data;
      if (data.items_updated > 0) {
        const details = data.updated_items.map(i => `${i.item_name}: ${i.new_available} available`).join(", ");
        toast.success(`${data.items_updated} of ${data.total_items} items updated — ${details}`);
      } else {
        toast.success(`All ${data.total_items} items already up to date`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recalculate stock");
    } finally {
      setRecalculating(false);
    }
  };

  const handleDeclineReturnRequest = async (requestId) => {
    if (!window.confirm("Decline this return/replacement request?")) return;
    try {
      await axios.post(`${API}/rental-return-requests/${requestId}/decline`, { admin_notes: "" });
      toast.success("Request declined");
      fetchData();
    } catch (error) {
      toast.error("Failed to decline request");
    }
  };

  const handleApprove = async (rentalId) => {
    try {
      await axios.post(`${API}/rentals/${rentalId}/approve`);
      toast.success("Rental request approved");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve rental");
    }
  };

  const handleDecline = async (rentalId) => {
    if (!window.confirm("Are you sure you want to decline this rental request?")) return;
    try {
      await axios.post(`${API}/rentals/${rentalId}/decline`);
      toast.success("Rental request declined");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline rental");
    }
  };

  const openEditDialog = (rental) => {
    setSelectedRental(rental);
    setEditData({
      customer_id: rental.customer_id,
      item_id: rental.item_id,
      quantity: rental.quantity,
      start_date: rental.start_date,
      expected_return_date: rental.expected_return_date || "",
      status: rental.status,
      condition_on_rental: rental.condition_on_rental || "good",
      notes: rental.notes || "",
    });
    setIsEditOpen(true);
  };

  const handleEditRental = async (e) => {
    e.preventDefault();
    if (!selectedRental) return;
    try {
      await axios.put(`${API}/rentals/${selectedRental.rental_id}`, editData);
      toast.success("Rental updated successfully");
      setIsEditOpen(false);
      setSelectedRental(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update rental");
    }
  };

  const resetNewRentalForm = () => {
    setNewRentalData({
      item_id: "",
      customer_id: "",
      quantity: 1,
      start_date: new Date().toISOString().split('T')[0],
      expected_return_date: "",
      condition_on_rental: "good",
      notes: "",
    });
  };

  const formatPrice = (price, period) => `R ${price.toFixed(2)}/${period}`;

  const getStatusBadge = (status) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status] || "bg-slate-100"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );

  // Pending approvals
  const pendingApprovals = rentals.filter(r => r.status === "pending_approval");

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="rental-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Rental Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage rentals and process returns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRecalculateStock}
            disabled={recalculating}
            data-testid="recalculate-stock-btn"
          >
            {recalculating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Recalculate Stock
          </Button>
          <Button onClick={() => setIsNewRentalOpen(true)} data-testid="new-rental-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Rental
          </Button>
        </div>
      </div>

      {/* Available Rental Items Summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-slate-700 mb-3">Available Rental Items</h3>
        <div className="flex flex-wrap gap-3">
          {allRentalItems.filter(i => i.is_active).map((item) => (
            <div
              key={item.item_id}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                item.available_quantity === 0
                  ? "bg-red-50 border-red-200 text-red-700"
                  : item.available_quantity <= Math.ceil(item.quantity * 0.25)
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"
              }`}
              data-testid={`rental-item-stock-${item.item_id}`}
            >
              <Package className="h-3.5 w-3.5" />
              <span className="font-medium">{item.name}</span>
              <span className="opacity-70">
                {item.available_quantity}/{item.quantity}
              </span>
            </div>
          ))}
          {allRentalItems.filter(i => i.is_active).length === 0 && (
            <p className="text-sm text-slate-400">No rental items configured</p>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Active</CardTitle>
            <Package className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{statCounts.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Delivered</CardTitle>
            <CheckCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{statCounts.delivered}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Overdue</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{statCounts.overdue}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Pending Approval</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{statCounts.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total</CardTitle>
            <Package className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-700">{statCounts.total}</div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals Alert */}
      {pendingApprovals.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Pending Approval ({pendingApprovals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingApprovals.map((rental) => (
                <div key={rental.rental_id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <div>
                    <span className="font-medium">{rental.customer_name}</span>
                    <span className="text-slate-500 mx-2">→</span>
                    <span>{rental.item_name}</span>
                    <span className="text-slate-500 ml-2">(Qty: {rental.quantity})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleApprove(rental.rental_id)}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDecline(rental.rental_id)}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Return/Replacement Requests */}
      {returnRequests.length > 0 && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="text-purple-800 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Return & Replacement Requests ({returnRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {returnRequests.map((req) => (
                <div key={req.request_id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-sm text-xs ${
                        req.request_type === "replacement" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                      }`}>
                        {req.request_type}
                      </Badge>
                      <span className="font-medium">{req.customer_name}</span>
                      <span className="text-slate-400">—</span>
                      <span>{req.item_name}</span>
                    </div>
                    {req.customer_notes && (
                      <p className="text-xs text-slate-500 mt-1">"{req.customer_notes}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => {
                      setApproveDialog({ open: true, request: req });
                      setApproveData({ collection_date: "", delivery_date: "", replacement_item_id: "", admin_notes: "" });
                    }}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeclineReturnRequest(req.request_id)}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rentals Table with Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <CardTitle className="shrink-0">Rentals</CardTitle>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by customer, rental ID, or item..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-rentals"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {STATUS_FILTERS.map((sf) => {
              const count = statusCounts[sf.value] || 0;
              const isActive = statusFilter === sf.value;
              return (
                <Button
                  key={sf.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(sf.value)}
                  className={`rounded-sm text-xs ${isActive ? "" : "text-slate-600"}`}
                  data-testid={`filter-${sf.value}`}
                >
                  {sf.label}
                  {count > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs rounded-full">
                      {count}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredRentals.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rental ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Technician</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Returned On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRentals.map((rental) => {
                  const effectiveStatus = getEffectiveStatus(rental);
                  const overdueFlag = isOverdue(rental);
                  const isReturned = effectiveStatus === "returned";
                  return (
                    <TableRow 
                      key={rental.rental_id} 
                      className={`cursor-pointer hover:bg-slate-50 ${overdueFlag ? "bg-red-50/50" : ""}`}
                      onClick={() => !isReturned && openEditDialog(rental)}
                      data-testid={`rental-row-${rental.rental_id}`}
                    >
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{rental.rental_id}</code>
                      </TableCell>
                      <TableCell>{getStatusBadge(effectiveStatus)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <div>
                            <div>{rental.customer_name}</div>
                            {rental.venue_name && (
                              <div className="text-xs text-slate-400 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{rental.venue_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{rental.item_name}</TableCell>
                      <TableCell>{rental.quantity}</TableCell>
                      <TableCell>
                        {(currentUser?.role === "admin" || currentUser?.role === "manager") && !isReturned ? (
                          <Select
                            value={rental.assigned_technician_id || ""}
                            onValueChange={(val) => handleAssignTechnician(rental.rental_id, val)}
                          >
                            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`assign-tech-${rental.rental_id}`}>
                              <SelectValue placeholder="Assign" />
                            </SelectTrigger>
                            <SelectContent>
                              {workers.map((w) => (
                                <SelectItem key={w.user_id} value={w.user_id}>
                                  {w.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm">{rental.assigned_technician_name || "—"}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {rental.start_date}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {rental.actual_return_date ? (
                            <span className="font-medium">{rental.actual_return_date.split("T")[0]}</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                          {overdueFlag && (
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500 ml-1" title="Overdue" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                            title="View Job Card"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          {!isReturned && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(rental)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {["pending_delivery", "delivered", "return_scheduled"].includes(rental.status) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                              title="Process Return via Job Card"
                              data-testid={`return-btn-${rental.rental_id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Return
                            </Button>
                          )}
                          {["delivered", "return_scheduled"].includes(rental.status) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-700 border-purple-300 hover:bg-purple-50"
                              onClick={() => {
                                setReplacementData({
                                  item_id: rental.item_id,
                                  quantity: rental.quantity,
                                  start_date: new Date().toISOString().split('T')[0],
                                  expected_return_date: "",
                                  assigned_technician_id: rental.assigned_technician_id || "",
                                  notes: `Replacement for ${rental.rental_id}`,
                                });
                                setReplacementDialog({ open: true, rental });
                              }}
                              title="Schedule Replacement"
                              data-testid={`replacement-btn-${rental.rental_id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Replace
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{searchQuery ? "No rentals match your search" : "No rentals found"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Rental Dialog */}
      <Dialog open={isNewRentalOpen} onOpenChange={setIsNewRentalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Rental</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRental} className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select
                value={newRentalData.customer_id}
                onValueChange={(value) => setNewRentalData({ ...newRentalData, customer_id: value, venue_id: "" })}
              >
                <SelectTrigger data-testid="rental-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {sortedCustomers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Venue selector — only show when customer has venues */}
            {newRentalData.customer_id && rentalVenues.length > 0 && (
              <div>
                <Label>Venue <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Select
                  value={newRentalData.venue_id}
                  onValueChange={(value) => setNewRentalData({ ...newRentalData, venue_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select venue (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific venue</SelectItem>
                    {rentalVenues.map((v) => (
                      <SelectItem key={v.venue_id} value={v.venue_id}>
                        {v.venue_name}{v.address?.city ? ` — ${v.address.city}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Rental Item *</Label>
              <Select
                value={newRentalData.item_id}
                onValueChange={(value) => setNewRentalData({ ...newRentalData, item_id: value })}
              >
                <SelectTrigger data-testid="rental-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {rentalItems.map((item) => (
                    <SelectItem key={item.item_id} value={item.item_id}>
                      {item.name} ({item.available_quantity} available) - {formatPrice(item.rental_price, item.billing_period)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={newRentalData.quantity}
                  onChange={(e) => setNewRentalData({ ...newRentalData, quantity: parseInt(e.target.value) || 1 })}
                  data-testid="rental-quantity"
                />
              </div>
              <div>
                <Label>Condition</Label>
                <Select
                  value={newRentalData.condition_on_rental}
                  onValueChange={(value) => setNewRentalData({ ...newRentalData, condition_on_rental: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={newRentalData.start_date}
                  onChange={(e) => setNewRentalData({ ...newRentalData, start_date: e.target.value })}
                  data-testid="rental-start-date"
                />
              </div>
              <div>
                <Label>Expected Return Date</Label>
                <Input
                  type="date"
                  value={newRentalData.expected_return_date}
                  onChange={(e) => setNewRentalData({ ...newRentalData, expected_return_date: e.target.value })}
                  data-testid="rental-return-date"
                />
              </div>
            </div>
            <div>
              <Label>Assign Technician</Label>
              <Select
                value={newRentalData.assigned_technician_id}
                onValueChange={(value) => setNewRentalData({ ...newRentalData, assigned_technician_id: value })}
              >
                <SelectTrigger data-testid="rental-technician-select">
                  <SelectValue placeholder="Select technician (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {workers.map((w) => (
                    <SelectItem key={w.user_id} value={w.user_id}>
                      {w.name} ({w.role === "worker" ? "Technician" : "Manager"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newRentalData.notes}
                onChange={(e) => setNewRentalData({ ...newRentalData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsNewRentalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-rental-btn">
                Create Rental
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Rental Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Rental</DialogTitle>
          </DialogHeader>
          {selectedRental && (
            <form onSubmit={handleEditRental} className="space-y-4">
              <div>
                <Label>Customer *</Label>
                <Select
                  value={editData.customer_id}
                  onValueChange={(value) => setEditData({ ...editData, customer_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedCustomers.map((c) => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rental Item *</Label>
                <Select
                  value={editData.item_id}
                  onValueChange={(value) => setEditData({ ...editData, item_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {allRentalItems.map((item) => (
                      <SelectItem key={item.item_id} value={item.item_id}>
                        {item.name} - {formatPrice(item.rental_price || 0, item.billing_period || "day")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editData.quantity}
                    onChange={(e) => setEditData({ ...editData, quantity: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editData.status}
                    onValueChange={(value) => setEditData({ ...editData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending_delivery">Pending Delivery</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="return_scheduled">Return Scheduled</SelectItem>
                      <SelectItem value="returned">Returned</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editData.start_date}
                    onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Expected Return Date</Label>
                  <Input
                    type="date"
                    value={editData.expected_return_date}
                    onChange={(e) => setEditData({ ...editData, expected_return_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Condition</Label>
                <Select
                  value={editData.condition_on_rental}
                  onValueChange={(value) => setEditData({ ...editData, condition_on_rental: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Rental notes..."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-rental-btn">
                  <Pencil className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Return/Replacement Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => !open && setApproveDialog({ open: false, request: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Approve {approveDialog.request?.request_type === "replacement" ? "Replacement" : "Return"} Request
            </DialogTitle>
          </DialogHeader>
          {approveDialog.request && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-sm p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Customer</span>
                  <span className="font-medium">{approveDialog.request.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Item</span>
                  <span>{approveDialog.request.item_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Type</span>
                  <Badge className={`rounded-sm text-xs ${
                    approveDialog.request.request_type === "replacement" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                  }`}>
                    {approveDialog.request.request_type}
                  </Badge>
                </div>
                {approveDialog.request.customer_notes && (
                  <div className="pt-1">
                    <span className="text-slate-500">Notes: </span>
                    <span className="italic">"{approveDialog.request.customer_notes}"</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Collection Date *</Label>
                <Input
                  type="date"
                  value={approveData.collection_date}
                  onChange={(e) => setApproveData({ ...approveData, collection_date: e.target.value })}
                  required
                />
              </div>
              {approveDialog.request.request_type === "replacement" && (
                <>
                  <div>
                    <Label>Replacement Item *</Label>
                    <Select
                      value={approveData.replacement_item_id}
                      onValueChange={(v) => setApproveData({ ...approveData, replacement_item_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select replacement item" />
                      </SelectTrigger>
                      <SelectContent>
                        {allRentalItems.map((item) => (
                          <SelectItem key={item.item_id} value={item.item_id}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Delivery Date *</Label>
                    <Input
                      type="date"
                      value={approveData.delivery_date}
                      onChange={(e) => setApproveData({ ...approveData, delivery_date: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={approveData.admin_notes}
                  onChange={(e) => setApproveData({ ...approveData, admin_notes: e.target.value })}
                  placeholder="Notes for the customer..."
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, request: null })}>
              Cancel
            </Button>
            <Button onClick={handleApproveReturnRequest}>
              Approve & Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Replacement Dialog */}
      <Dialog open={replacementDialog.open} onOpenChange={(open) => !open && setReplacementDialog({ open: false, rental: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Replacement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {replacementDialog.rental && (
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
                <p className="font-medium text-purple-800">Replacing rental for: {replacementDialog.rental.customer_name}</p>
                <p className="text-purple-600">Original: {replacementDialog.rental.item_name} x{replacementDialog.rental.quantity}</p>
                <Badge className="mt-1 bg-purple-100 text-purple-700">Replacement</Badge>
              </div>
            )}
            <div>
              <Label>Rental Item *</Label>
              <Select
                value={replacementData.item_id}
                onValueChange={(value) => setReplacementData({ ...replacementData, item_id: value })}
              >
                <SelectTrigger data-testid="replacement-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {allRentalItems.filter(i => i.is_active && i.available_quantity > 0).map((item) => (
                    <SelectItem key={item.item_id} value={item.item_id}>
                      {item.name} ({item.available_quantity} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={replacementData.quantity}
                  onChange={(e) => setReplacementData({ ...replacementData, quantity: parseInt(e.target.value) || 1 })}
                  data-testid="replacement-quantity"
                />
              </div>
              <div>
                <Label>Assign Technician</Label>
                <Select
                  value={replacementData.assigned_technician_id}
                  onValueChange={(value) => setReplacementData({ ...replacementData, assigned_technician_id: value })}
                >
                  <SelectTrigger data-testid="replacement-tech-select">
                    <SelectValue placeholder="Select technician" />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.map((w) => (
                      <SelectItem key={w.user_id} value={w.user_id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delivery Date *</Label>
                <Input
                  type="date"
                  value={replacementData.start_date}
                  onChange={(e) => setReplacementData({ ...replacementData, start_date: e.target.value })}
                  data-testid="replacement-start-date"
                />
              </div>
              <div>
                <Label>Expected Return Date</Label>
                <Input
                  type="date"
                  value={replacementData.expected_return_date}
                  onChange={(e) => setReplacementData({ ...replacementData, expected_return_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={replacementData.notes}
                onChange={(e) => setReplacementData({ ...replacementData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplacementDialog({ open: false, rental: null })}>
              Cancel
            </Button>
            <Button onClick={handleCreateReplacement} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-replacement-btn">
              Create Replacement Rental
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalManagement;
